import { Archive, FilterList } from '@mui/icons-material';
import { Box, Breadcrumbs, Stack, Typography } from '@mui/material';
import type {
  GridPaginationModel,
  GridSortDirection,
  GridSortModel,
} from '@mui/x-data-grid';
import {
  ActionMenu,
  DescriptionListWrapper,
  FilterButton,
  Match,
  SearchField,
  Show,
  Switch,
  TraditionalPagination,
  useIsMobile,
  usePersistedDismissable,
} from '@side/pantry';
import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { globalInteractions } from '@/common/atoms/global';
import { useReferrals } from '@/common/hooks/referrals/useReferrals';
import { FullSizeFlexContainer } from '@/components/FullSizeFlexContainer';
import { Link as BreadcrumbLink } from '@/components/Link';
import { TextSkeleton } from '@/components/TextSkeleton';
import { TXM_REFERRALS_PATH } from '@/constants';
import { contentContainerSx } from '@/theme/contentContainerSx';
import { GlobalToggle } from '@/views/Interactions/components/GlobalToggle';
import { useInitializeInteractionsFilters } from '@/views/Interactions/hooks/useInitializeInteractionsFilters';
import type { InteractionFilter } from '@/views/Interactions/state/filters';
import {
  getInteractionsFiltersKey,
  interactionsAgreementStatusesState,
  interactionsFiltersState,
  interactionsPagingState,
  interactionsReferralClientTypesState,
  interactionsSearchState,
  useInteractionsSortState,
  useSetInteractionsSearchState,
} from '@/views/Interactions/state/filters';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import { CreateReferralButton } from './CreateReferralButton';
import { FiltersDialog } from './FiltersDialog';
import { NoReferralsExist } from './NoReferralsExist';
import { ReferralCard } from './ReferralCard';
import type { ReferralTableRowSkeleton } from './ReferralsTable';
import { ReferralsTable } from './ReferralsTable';

const DEFAULT_REFERRAL_SORT = 'endDate:asc';

const VALID_REFERRAL_SORT_FIELDS = new Set([
  'startDate',
  'endDate',
  'transactingAgent',
  'referringAgent',
]);

interface BuildQueryParamsArgs {
  pageSize: number;
  currentPage: number;
  searchQuery: string | undefined;
  sortField: string;
  sortOrder: string;
  filters: readonly InteractionFilter[];
  showGlobalInteractions: boolean;
  isArchived: boolean;
}

function buildReferralsQueryParams({
  pageSize,
  currentPage,
  searchQuery,
  sortField,
  sortOrder,
  filters,
  showGlobalInteractions,
  isArchived,
}: BuildQueryParamsArgs) {
  const filterValues = filters.map((f) => f.value);
  filterValues.push(`isArchived:${isArchived}`);

  const facets = filterValues.join(',');

  const isValidSort = VALID_REFERRAL_SORT_FIELDS.has(sortField);

  return {
    limit: pageSize,
    offset: currentPage * pageSize,
    search: searchQuery || undefined,
    sort:
      isValidSort && sortField && sortOrder
        ? `${sortField}:${sortOrder}`
        : DEFAULT_REFERRAL_SORT,
    facets,
    isGlobal: showGlobalInteractions,
  };
}

function LoadingReferralCards() {
  return (
    <>
      {Array.from(new Array(3)).map((_, i) => (
        <DescriptionListWrapper
          Button={<TextSkeleton width={100} height={28} sx={{ my: 0.75 }} />}
          // biome-ignore lint/suspicious/noArrayIndexKey: Skeletons do not move
          key={i}
          TitleAdornment={<TextSkeleton width={100} height={20} />}
          title={<TextSkeleton width={150} height={20} />}
        >
          <dl>
            <dt>
              <TextSkeleton width={230} />
            </dt>
            <dd>
              <TextSkeleton width={250} />
            </dd>
            <dt>
              <TextSkeleton width={200} />
            </dt>
            <dd>
              <TextSkeleton width={220} />
            </dd>
          </dl>
        </DescriptionListWrapper>
      ))}
    </>
  );
}

interface Props {
  isArchived?: boolean;
}

export function ReferralsList({ isArchived = false }: Props) {
  const filtersStateKey = getInteractionsFiltersKey('referral', {
    archived: isArchived,
  });
  useInitializeInteractionsFilters(filtersStateKey);

  const isMobile = useIsMobile('md');
  const router = useRouter();
  const [_, { dismiss: dismissNewChipOnCommunityPage }] =
    usePersistedDismissable({
      key: 'community-landing-new-referral-chip-visibility',
    });

  const trackEvent = useTrackReferralsAnalytics();

  useEffectOnce(() => {
    dismissNewChipOnCommunityPage();

    trackEvent(TrackReferralsEvent.ReferralsLanding);
  });

  /**
   * Pagination
   */
  const [paginationState, setPaginationState] = useAtom(
    interactionsPagingState(filtersStateKey),
  );
  const currentPage = paginationState?.page ? paginationState.page - 1 : 0;
  const pageSize = paginationState?.perPage ?? 25;

  const handlePaginationChange = ({
    page,
    pageSize: perPage,
  }: GridPaginationModel) => {
    setPaginationState((currVal) => ({
      page: page + 1,
      perPage,
      totalItems: currVal?.totalItems ?? 0,
      totalPages: currVal?.totalPages ?? 0,
    }));
  };

  /**
   * Sorting
   */
  const [sortValue, setSortValue] = useInteractionsSortState(
    filtersStateKey,
    DEFAULT_REFERRAL_SORT,
  );
  const [sortField, sortOrder] = sortValue.split(':');

  /**
   * Filtering
   */
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const filters = useAtomValue(interactionsFiltersState(filtersStateKey));
  const selectedClientTypes = useAtomValue(
    interactionsReferralClientTypesState(filtersStateKey),
  );
  const searchQuery = useAtomValue(interactionsSearchState(filtersStateKey));
  const setSearch = useSetInteractionsSearchState(filtersStateKey);
  const selectedAgreementStatuses = useAtomValue(
    interactionsAgreementStatusesState(filtersStateKey),
  );
  const showGlobalInteractions = useAtomValue(globalInteractions);

  /**
   * Data fetching with parameters
   */
  const queryParams = buildReferralsQueryParams({
    pageSize,
    currentPage,
    searchQuery,
    sortField,
    sortOrder,
    filters,
    showGlobalInteractions,
    isArchived,
  });

  const { count: rowCount, isLoading, referrals } = useReferrals(queryParams);
  const pageCount = Math.ceil(rowCount / pageSize);

  // Check if any user-applied filters or search are active (excludes the
  // implicit isArchived facet so the true zero-state can still render).
  const hasFiltersApplied = !!(searchQuery || filters.length > 0);

  // Show true zero state only if no results AND no filters applied AND not loading
  // If filters are applied and no results, show empty table with controls
  // Match GraphQL behavior where areReferralsPresent was true during loading
  const areReferralsPresent = isLoading || rowCount > 0 || hasFiltersApplied;

  const skeletonRows: ReferralTableRowSkeleton[] = Array.from(
    { length: pageSize },
    (_, i) => ({ id: `skeleton-${i}` }),
  );

  const sortModel: GridSortModel = useMemo(
    () =>
      sortField && sortOrder
        ? [
            {
              field: sortField,
              sort: sortOrder as GridSortDirection,
            },
          ]
        : [],
    [sortField, sortOrder],
  );

  const viewArchivedMenuOptions = [
    {
      label: 'View Archived Referrals',
      icon: <Archive />,
      onClick: () => {
        void router.push(`${TXM_REFERRALS_PATH}/archived`);
      },
    },
  ];

  return (
    <FullSizeFlexContainer
      sx={{
        backgroundColor: 'white',
        height: 'min-content',
        paddingBottom: 2,
        ...contentContainerSx,
        maxWidth: '100%',
      }}
    >
      <Stack
        sx={{
          flexDirection: 'column',
          gap: 2.5,
          paddingTop: {
            xs: 2,
            md: 3,
          },
        }}
      >
        <Show when={isArchived}>
          <Breadcrumbs sx={{ mb: 1 }}>
            <BreadcrumbLink href={TXM_REFERRALS_PATH}>
              Client Referrals
            </BreadcrumbLink>
            <Typography>Archived Client Referrals</Typography>
          </Breadcrumbs>
        </Show>

        <Stack
          data-testid="Referrals__Header"
          sx={{
            alignItems: isMobile ? 'stretch' : 'start',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ mb: 1 }} variant="h1">
              {isArchived ? 'Archived Client Referrals' : 'Client Referrals'}
            </Typography>

            <Show when={!isArchived}>
              <Typography
                component="h2"
                sx={{ color: 'text.secondary' }}
                variant="body1"
              >
                Create and organize client referrals
              </Typography>
            </Show>
          </Box>

          <Show when={!isArchived}>
            <CreateReferralButton
              onClick={() => {
                trackEvent(TrackReferralsEvent.ClickAddReferralButton, {
                  eventTarget: 'add_referral_button_top',
                });
              }}
            />
          </Show>
        </Stack>

        <Stack
          data-testid="Referrals__QueryControls"
          sx={{
            flexDirection: 'row',
            gap: 1,
            justifyContent: 'space-between',
            mb: {
              xs: 2,
              md: 2.5,
            },
          }}
        >
          <Show
            fallback={<Box sx={{ height: '44px' }} />}
            when={areReferralsPresent}
          >
            <Stack direction="row" sx={{ flexGrow: 1 }}>
              <Box sx={{ flexGrow: 1, maxWidth: 600 }}>
                <SearchField
                  aria-label="Search client referrals"
                  autoFocus={!isMobile}
                  debounceInterval={300}
                  fullWidth
                  handleSearchChange={setSearch}
                  placeholder="Search client referrals"
                  onClear={() => setSearch('')}
                />
              </Box>
              <GlobalToggle
                sx={{ ml: { xs: 2, md: 4 }, mr: 2 }}
                typographyProps={{
                  variant: 'subtitle2',
                  color: 'text.secondary',
                }}
                labelPlacement="end"
              />
            </Stack>

            <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
              <FilterButton
                filterCount={filters.length}
                onClick={() => {
                  setIsApplyingFilters(true);
                  trackEvent(TrackReferralsEvent.ClickFiltersButton);
                }}
                size="large"
                startIcon={<FilterList fontSize="small" />}
              >
                Filters
              </FilterButton>
              <Show when={!isArchived}>
                <ActionMenu
                  options={viewArchivedMenuOptions}
                  variant="contained"
                  title="Client Referrals"
                />
              </Show>
            </Stack>
          </Show>
        </Stack>
      </Stack>

      <Switch>
        <Match when={!areReferralsPresent}>
          <NoReferralsExist archived={isArchived} />
        </Match>

        <Match when={isMobile && areReferralsPresent}>
          <Stack
            sx={{
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Stack
              data-testid="Referrals__ReferralsCards"
              sx={{
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Show when={!isLoading} fallback={<LoadingReferralCards />}>
                {referrals.map((referral) => {
                  return (
                    <ReferralCard
                      key={referral.id}
                      isArchived={isArchived}
                      referral={referral}
                    />
                  );
                })}
              </Show>
            </Stack>

            <TraditionalPagination
              isZeroIndexed
              onPageChange={(page) => {
                handlePaginationChange({
                  page,
                  pageSize,
                });
              }}
              page={currentPage}
              perPage={pageSize}
              totalItems={rowCount}
              totalPages={pageCount}
            />
          </Stack>
        </Match>

        <Match when={!isMobile}>
          <Box data-testid="Referrals__ReferralsTable">
            <ReferralsTable
              isArchived={isArchived}
              loading={isLoading}
              paginationModel={{
                page: currentPage,
                pageSize,
              }}
              rowCount={rowCount}
              rows={isLoading ? skeletonRows : referrals}
              setPaginationModel={handlePaginationChange}
              setSortModel={([
                { field, sort } = { field: 'endDate', sort: 'asc' },
              ]) => setSortValue(`${field}:${sort}`)}
              sortModel={sortModel}
            />
          </Box>
        </Match>
      </Switch>

      <FiltersDialog
        isOpen={isApplyingFilters}
        onChangeSort={setSortValue}
        onClose={() => setIsApplyingFilters(false)}
        selectedAgreementStatuses={selectedAgreementStatuses}
        selectedClientTypes={selectedClientTypes}
        selectedSort={sortValue}
      />
    </FullSizeFlexContainer>
  );
}
